import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { Field } from '#/components/Form/Field/Field';
import type { FieldCustomCompare } from '#/components/Form/Field/FieldCustomCompare';
import { useFormContext } from '#/components/Form/FormContext';
import { ListingRte } from '#/modules/listing/components/Rte/ListingRte';
import type { ListingType } from '#/types/ListingType';
import { hyperlinkHtml } from '#/utils/string/hyperlinkHtml';

type Props<TValues extends Record<string, unknown>> = {
  name: string;
  label: string;
  placeholder?: string;
  description?: ReactNode;
  intro?: string;
  outro?: string;
  showAtsLinkWarnings?: boolean;
  onChange?: (newValue: string) => void;
  onPaste?: (pastedText: string) => void;
  matchGeneralError?: string;
  customCompare?: FieldCustomCompare<TValues>;
  qaId?: string;
  hideErrorLabel?: boolean;
  noMargin?: boolean;
  compactLabel?: boolean;
  disabled?: boolean;
  listingType?: ListingType | undefined;
};

export function ListingRteField<TValues extends Record<string, unknown>>({
  name,
  label,
  placeholder,
  description,
  intro,
  outro,
  showAtsLinkWarnings,
  onChange,
  onPaste,
  matchGeneralError,
  customCompare,
  qaId,
  disabled,
  noMargin,
  compactLabel,
  hideErrorLabel,
  listingType,
}: Props<TValues>) {
  const { updateValue, submitting } = useFormContext();

  const handleChange = useCallback(
    (newValue: string) => {
      updateValue(name, hyperlinkHtml(newValue));
      onChange?.(newValue);
    },
    [onChange, updateValue, name],
  );

  return (
    <Field
      name={name}
      label={label}
      description={description}
      matchGeneralError={matchGeneralError}
      customCompare={customCompare}
      noMargin={noMargin}
      compactLabel={compactLabel}
      hideErrorLabel={hideErrorLabel}
      renderInput={({ id, value, qaId: fieldQaId, hasError }) => (
        <ListingRte
          key={id || name}
          id={id}
          intro={intro}
          outro={outro}
          initialValue={value}
          placeholder={placeholder}
          onChange={handleChange}
          onPasteText={onPaste}
          showAtsLinkWarnings={Boolean(showAtsLinkWarnings)}
          disabled={disabled || submitting}
          qaId={qaId ?? fieldQaId}
          hasError={hasError}
          listingType={listingType}
          labelId={`${id}-label`}
        />
      )}
    />
  );
}
